import React, { useState, useEffect } from "react";
import { Grid } from "antd";
import isEmpty from "lodash/isEmpty";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from "recharts";
import Helpers from "../../services/helpers";
import { entitiesToPayload } from "./barChart.assets";
import FullscreenControl from "./FullscreenControl";
import AddAlertModal from "../modals/AddAlertModal";
import { useCardsContext } from "../contexts/cardcontext";
import { useSearchContext } from "components/contexts/searchcontext.ts";
import RangePicker from "./RangePicker";
import { usePopularChartContext } from "../contexts/popularchartcontext";
import PopularChartLegend from "./PopularCardChart/PopularChartLegend";
import { EMPTY_ALERT_MODAL_DATA, ENTITY_TYPES } from "utils/constants";
import { useWaxesContext } from "components/contexts/WaxContext";

const dummyData = [{ color: "transparent", dataKey: "foo_endAverageValue", endAverageValue: -1, value: -1 }];

const customTooltip = (props) => {
  const payload = props.payload && props.payload.length ? props.payload[0] : null;
  return payload && payload.value !== -1 ? (
    <div
      className={"recharts-default-tooltip"}
      style={{
        background: "white",
        padding: "10px",
        border: "1px solid rgb(204,204,204)",
      }}
    >
      <p className={"recharts-tooltip-label"} style={{ color: payload.payload.color }}>
        {props.label}
      </p>

      <span className={"recharts-tooltip-item-name"}>Last Avg Sale Price:&nbsp;</span>
      <span className={"recharts-tooltip-item-value"}>{Helpers.formatMoney(props.payload[0].value, 2)}</span>
    </div>
  ) : null;
};

export default function MMBarChart({ model, entities, entityType = ENTITY_TYPES.CARD }) {
  const { lg } = Grid.useBreakpoint();
  const searchContext = useSearchContext();
  const { setHasAlert: setHasAlertCards } = useCardsContext();
  const { setHasAlert: setHasAlertWax } = useWaxesContext();
  const { hiddenLines, lineProps } = usePopularChartContext();
  const [alertModalData, setAlertModalData] = useState(EMPTY_ALERT_MODAL_DATA);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [payload, setPayload] = useState([]);
  const [data, setData] = useState([]);
  const [hovering, setHovering] = useState(null);

  useEffect(() => {
    let newPayload = entitiesToPayload(entities, entityType, model.bars[0].dataKey, lineProps);
    if (entityType === ENTITY_TYPES.CARD) {
      newPayload = newPayload.sort(Helpers.sortCardsByNameThenGrade);
    }

    const newData = model.dataFn(newPayload, entityType);

    setPayload(newPayload);
    setData(newData);
  }, [model, entities, lineProps, entityType]);

  useEffect(() => {
    const newData = model.dataFn(
      payload.filter(({ cardId, sealedWaxId }) => {
        switch (entityType) {
          case ENTITY_TYPES.CARD:
            return !hiddenLines[cardId];
          case ENTITY_TYPES.SEALED_WAX:
            return !hiddenLines[sealedWaxId];
          default:
            return true;
        }
      }),
      entityType,
    );

    setData(newData);
    setHovering(null);
  }, [payload, hiddenLines]);

  if (isEmpty(data) && !searchContext.initialSearchPerformed) {
    return (
      <div className="saved-well">
        <p style={{ textAlign: "left", fontSize: 16, marginBottom: 0, fontWeight: "bold" }}>
          Use the search on the left to get started.
        </p>
      </div>
    );
  }

  const chartLegendHoverFns = {
    onEnter: (el) => {
      let target = el.target.closest("li");
      if (!target) return;

      const { dataset } = target;
      if (dataset && dataset.key) {
        const [entityId] = dataset.key.split("_");

        if (hiddenLines[entityId] && hiddenLines[entityId].visible) {
          setHovering(dataset.key);
        }
      }
    },
    onLeave: () => {
      setHovering(null);
    },
  };

  const calcChartHeight = (entities) => {
    if (entities.length > 8) return 700;
    return 500;
  };

  return (
    <div
      className={isFullscreen ? "chart-fullscreen" : "chart-notfullscreen"}
      style={{ height: calcChartHeight(entities) }}
    >
      <div className="graph-controls">
        <RangePicker displayQuickOptions={lg} />

        <AddAlertModal
          cardId={alertModalData.cardId}
          sealedWaxId={alertModalData.sealedWaxId}
          visible={alertModalData.visible}
          defaultData={{ name: alertModalData.name }}
          onCancel={() => setAlertModalData(EMPTY_ALERT_MODAL_DATA)}
          onSubmit={() => {
            const newPayload = payload.map((item) => {
              const identifier = {
                [ENTITY_TYPES.CARD]: "cardId",
                [ENTITY_TYPES.SEALED_WAX]: "sealedWaxId",
              }[entityType];

              if (Number(item[identifier]) === Number(alertModalData[identifier])) {
                item.payload.hasAlert = true;
              }

              return item;
            });

            setPayload(newPayload);
            if (entityType === ENTITY_TYPES.CARD) {
              setHasAlertCards(Number(alertModalData.cardId));
            }
            if (entityType === ENTITY_TYPES.SEALED_WAX) {
              setHasAlertWax(Number(alertModalData.sealedWaxId));
            }
          }}
        />
        <FullscreenControl
          isFullscreen={isFullscreen}
          enterAction={() => setIsFullscreen(true)}
          exitAction={() => setIsFullscreen(false)}
        />
      </div>

      <div style={{ position: "relative", height: "98%", width: "100%" }}>
        <ResponsiveContainer width="100%" height="98%">
          <BarChart data={isEmpty(data) ? dummyData : data} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray={model.cartesianGridStrokeDasharray} />

            <XAxis dataKey={model.xAxisDataKey} tick={model.xAxisTick} />

            <YAxis tickFormatter={model.yAxisTickFormatter} />

            <Tooltip
              formatter={(value) => Helpers.formatMoney(value, 2)}
              content={customTooltip}
              cursor={{ fill: "transparent" }}
            />

            {model.bars.map((bar) => (
              <Bar maxBarSize={120} key={bar.dataKey} dataKey={bar.dataKey} fill="#555">
                {(isEmpty(data) ? dummyData : data).map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry?.color}
                    opacity={hovering === null ? 1 : entry?.dataKey === hovering ? 1 : 0.5}
                  />
                ))}
              </Bar>
            ))}

            {entities.length > 0 && (
              <Legend
                content={() => (
                  <PopularChartLegend
                    entityType={entityType}
                    payload={payload}
                    hoverFns={chartLegendHoverFns}
                    setAlertModalData={setAlertModalData}
                  />
                )}
                height={entities.length > 4 ? 264 : 132}
                wrapperStyle={{ bottom: 26 }}
                layout="horizontal"
                verticalAlign="bottom"
              />
            )}
          </BarChart>
        </ResponsiveContainer>
        {isEmpty(data) && (
          <b style={{ position: "absolute", top: "50%", left: lg ? "40%" : "20%", textAlign: "center" }}>
            No Results - Please Try Expanding Your Date Range
          </b>
        )}
      </div>
    </div>
  );
}
